/**
 * TimelineScale Component
 * 
 * Displays the timeline scale header with month markers and date range
 */

import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface TimelineScaleProps {
  /** Date range for the timeline */
  dateRange: { start: Date; end: Date }
  
  /** Width of the timeline content area */
  timelineWidth: number
  
  /** Left offset to align with timeline content */
  leftOffset: number
  
  /** Height of the scale header */
  height?: number
}

// ============================================================================
// TIMELINE SCALE COMPONENT
// ============================================================================

const TimelineScale: React.FC<TimelineScaleProps> = ({
  dateRange,
  timelineWidth,
  leftOffset,
  height = 60
}) => {
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  const monthMarkers = useMemo(() => {
    const markers: Array<{
      date: Date
      label: string
      shortLabel: string
      position: number
    }> = []
    
    // Start from the beginning of the month containing the start date
    const current = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth(), 1)
    
    // Calculate total timeline duration in days
    const totalDays = Math.ceil(
      (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
    )
    
    while (current <= dateRange.end) {
      // Calculate position as percentage of timeline width
      const daysSinceStart = Math.ceil(
        (current.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
      )
      
      const position = (daysSinceStart / totalDays) * timelineWidth
      
      // Only add markers that are within the visible timeline
      if (position >= 0 && position <= timelineWidth) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December']
        const shortMonthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                                'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

        markers.push({
          date: new Date(current),
          label: `${monthNames[current.getMonth()]} ${current.getFullYear()}`,
          shortLabel: shortMonthNames[current.getMonth()],
          position
        })
      }
      
      // Move to next month
      current.setMonth(current.getMonth() + 1)
    }
    
    return markers
  }, [dateRange, timelineWidth])
  
  const dateRangeLabel = useMemo(() => {
    const startMonth = dateRange.start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    // For extended timelines, show a more focused label
    const totalMonths = Math.ceil(
      (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
    )

    if (totalMonths > 4) {
      // Extended timeline - show starting point and indicate scrollability
      return `${startMonth} onwards (scroll to view more)`
    } else {
      // Short timeline - show full range
      const endMonth = dateRange.end.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      return `${startMonth} - ${endMonth}`
    }
  }, [dateRange])
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <Box
      sx={{
        position: 'relative',
        height,
        width: '100%',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper'
      }}
    >
      {/* Date Range Label */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 16,
          zIndex: 10
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        >
          {dateRangeLabel}
        </Typography>
      </Box>
      
      {/* Timeline Scale Container */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: leftOffset,
          width: timelineWidth,
          height: '100%',
          overflow: 'visible' // Allow month labels to extend beyond container if needed
        }}
      >
        {/* Month Markers */}
        {monthMarkers.map((marker, index) => (
          <Box
            key={`${marker.date.getTime()}-${index}`}
            sx={{
              position: 'absolute',
              left: marker.position,
              top: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transform: 'translateX(-50%)', // Center the marker
              minWidth: 120 // Increased to accommodate full month names like "September 2025"
            }}
          >
            {/* Vertical Line */}
            <Box
              sx={{
                width: '1px',
                height: '100%',
                backgroundColor: 'divider',
                opacity: 0.5
              }}
            />
            
            {/* Month Label */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                backgroundColor: 'background.paper',
                px: 1,
                borderRadius: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textAlign: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                {marker.label}
              </Typography>
            </Box>
          </Box>
        ))}
        
        {/* Today Marker (if within range) */}
        {(() => {
          const today = new Date()
          if (today >= dateRange.start && today <= dateRange.end) {
            const totalDays = Math.ceil(
              (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
            )
            const daysSinceStart = Math.ceil(
              (today.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
            )
            const position = (daysSinceStart / totalDays) * timelineWidth
            
            return (
              <Box
                sx={{
                  position: 'absolute',
                  left: position,
                  top: 0,
                  height: '100%',
                  transform: 'translateX(-50%)',
                  zIndex: 5
                }}
              >
                {/* Today Line */}
                <Box
                  sx={{
                    width: '2px',
                    height: '100%',
                    backgroundColor: 'primary.main',
                    opacity: 0.8
                  }}
                />
                
                {/* Today Label */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 32,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}
                >
                  TODAY
                </Box>
              </Box>
            )
          }
          return null
        })()}
      </Box>
    </Box>
  )
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

export default React.memo(TimelineScale)

// ============================================================================
// COMPONENT DISPLAY NAME
// ============================================================================

TimelineScale.displayName = 'TimelineScale'
